import { DocumentType } from '@synergy/models';
import { getUrlSegment } from '@synergy/utils';
import { request } from '@synergy/api';

/**
 * The search behind the command palette (#2132, wave 3).
 *
 * No endpoint of its own: every list already searches its own documents through
 * the search engine, and asking those endpoints for the first few hits is the
 * same search the user would get by opening the list and typing there. What was
 * missing was only that nothing asked them all at once.
 */

export interface OmniSearchHit {
    id: number;
    documentType: DocumentType;
    /** The document number, or a stand-in when it has none yet. */
    number: string;
    /** Who it is with — customer or supplier, whichever the type carries. */
    party?: string;
}

export interface OmniSearchMasterDataHit {
    id: number;
    kind: 'account' | 'contact';
    label: string;
    sub?: string;
}

export interface OmniSearchResult {
    documents: OmniSearchHit[];
    masterData: OmniSearchMasterDataHit[];
    /** True if any type answered with more than we show. */
    truncated: boolean;
}

const TYPES: DocumentType[] = [
    DocumentType.PurchaseOrder,
    DocumentType.Invoice,
    DocumentType.Inquiry,
    DocumentType.SalesOrder,
    DocumentType.Demand,
];

/** Few enough that five types together still fit in the palette. */
const PER_TYPE = 4;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const readParty = (item: any): string | undefined => {
    const account =
        item?.supplierAccount ?? item?.customerAccount ?? item?.recipientAccount ?? item?.account ?? undefined;

    return account?.name ?? item?.customerName ?? item?.supplierName ?? undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const readNumber = (item: any): string => {
    const value = item?.prefixedNumber ?? item?.number ?? item?.title;

    // A document without a number is a draft; saying "#37" would invent one.
    return value ? String(value) : 'Entwurf';
};

export class OmniSearchApi {
    constructor(protected baseUrl: string) {}

    public async search(term: string, signal?: AbortSignal): Promise<OmniSearchResult> {
        if (term.length < 2) {
            return { documents: [], masterData: [], truncated: false };
        }

        const masterData = this.searchMasterData(term, signal);

        const answers = await Promise.all(
            TYPES.map(async documentType => {
                try {
                    const response = await request.get(`${this.baseUrl}/${getUrlSegment(documentType)}`, {
                        params: { search: term, limit: PER_TYPE, page: 1 },
                        signal,
                    });

                    return { documentType, data: response.data };
                } catch {
                    // One type failing must not take the whole palette with it.
                    return { documentType, data: undefined };
                }
            })
        );

        const documents: OmniSearchHit[] = [];
        let truncated = false;

        answers.forEach(({ documentType, data }) => {
            const items = data?.items ?? [];

            if ((data?.total ?? 0) > items.length) {
                truncated = true;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            items.forEach((item: any) =>
                documents.push({
                    id: item.id,
                    documentType,
                    number: readNumber(item),
                    party: readParty(item),
                })
            );
        });

        return { documents, masterData: await masterData, truncated };
    }

    /**
     * Customers, suppliers and contacts, from the quick-search endpoints that
     * the autocomplete fields already use.
     */
    private async searchMasterData(term: string, signal?: AbortSignal): Promise<OmniSearchMasterDataHit[]> {
        const ask = async (path: string): Promise<Record<string, unknown>[]> => {
            try {
                const response = await request.get(`${this.baseUrl}/${path}`, {
                    params: { search: term, limit: PER_TYPE, page: 1 },
                    signal,
                });

                return response.data?.items ?? [];
            } catch {
                return [];
            }
        };

        const [accounts, contacts] = await Promise.all([ask('quicksearch/accounts'), ask('quicksearch/contacts')]);

        return [
            ...accounts.map(account => ({
                id: Number(account['id']),
                kind: 'account' as const,
                label: String(account['name'] ?? ''),
                sub: 'Konto',
            })),
            ...contacts.map(contact => ({
                id: Number(contact['id']),
                kind: 'contact' as const,
                label: String(contact['fullName'] ?? ''),
                sub: String(contact['email'] ?? 'Kontakt'),
            })),
        ].filter(hit => '' !== hit.label);
    }
}
